import { PlusInSquareIcon } from "assets/icons";
import { If } from "components";
import { ProductsSelectorTable } from "modules/products";
import moment from "moment";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  CounterInputControls,
  DateRangeInput,
  Input,
  Select
} from "ui";

import {
  SharingScheduleRules,
  SharingScheduleRuleTimeFrames
} from "../../../constants";
import {
  RULES_OPTIONS,
  RULE_TIMEFRAME_OPTIONS
} from "./SharingScheduleForm.constants";
import {
  SharingScheduleFormProps,
  SharingScheduleFormValues
} from "./SharingScheduleForm.types";

import styles from "./SharingScheduleForm.module.scss";

const SharingScheduleForm = ({
  formId,
  onSelectProductsClick,
  onSubmit,
  editableSchedule: _editableSchedule
}: SharingScheduleFormProps) => {
  const { t } = useTranslation();
  const { control, register, watch, handleSubmit, formState } =
    useFormContext<SharingScheduleFormValues>();

  const ruleWatch = watch("rule");
  const ruleRuleTimeframeWatch = watch("ruleTimeframe");
  const numberOfProductsWatch = watch("numberOfProducts");
  const productVariantsWatch = watch("productVariants");

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <Input
        {...register("name")}
        label={t("Name")}
        errorMessage={formState.errors.name?.message}
      />

      <div className={styles.form_separatorLine} />

      <Controller
        control={control}
        name="dateRange"
        render={({ field: { value, onChange }, formState: { errors } }) => (
          <DateRangeInput
            label={t("First/Last Date")}
            value={value}
            onChange={onChange}
            errorMessage={errors.dateRange?.message}
            minDate={moment().utc().toDate()}
          />
        )}
      />

      <div className={styles.form_separatorLine} />

      <div className={styles.form_rulesBlock}>
        <Controller
          control={control}
          name="numberOfProducts"
          render={({
            field: { value, onChange },
            formState: { errors: _errors }
          }) => (
            <Input
              label={t("Products Qty")}
              type="number"
              value={value}
              action={
                <CounterInputControls
                  value={value}
                  onChange={onChange}
                  min={4}
                  max={6}
                />
              }
            />
          )}
        />

        <Controller
          control={control}
          name="rule"
          render={({ field: { value, onChange } }) => (
            <Select
              options={RULES_OPTIONS}
              value={value}
              onChange={onChange}
              placeholder={t("Select rule")}
              label={t("Rule")}
            />
          )}
        />

        <Controller
          control={control}
          name="ruleTimeNumber"
          render={({
            field: { value, onChange },
            formState: { errors: _errors }
          }) => (
            <Input
              label={t("Timeframes Qty")}
              type="number"
              value={value}
              onChange={onChange}
              min={1}
              max={366}
              disabled={
                ruleRuleTimeframeWatch ===
                  SharingScheduleRuleTimeFrames.ForAllTime ||
                ruleWatch === SharingScheduleRules.ManualSelectionOfProducts
              }
              action={
                <CounterInputControls
                  value={value}
                  disabled={
                    ruleRuleTimeframeWatch ===
                      SharingScheduleRuleTimeFrames.ForAllTime ||
                    ruleWatch === SharingScheduleRules.ManualSelectionOfProducts
                  }
                  onChange={onChange}
                  min={1}
                  max={366}
                />
              }
            />
          )}
        />

        <Controller
          control={control}
          name="ruleTimeframe"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              label={t("Rule's timeframe")}
              options={RULE_TIMEFRAME_OPTIONS}
              value={value}
              disabled={
                ruleWatch === SharingScheduleRules.ManualSelectionOfProducts
              }
              onChange={onChange}
              placeholder={t("Select rule's timeframe")}
              errorMessage={error?.message}
            />
          )}
        />
      </div>

      <div className={styles.form_separatorLine} />

      <If
        condition={ruleWatch === SharingScheduleRules.ManualSelectionOfProducts}
      >
        <>
          <Controller
            control={control}
            name="productVariants"
            render={({ field: { value, onChange } }) => (
              <If condition={value.length !== 0}>
                <>
                  <ProductsSelectorTable
                    products={value}
                    onChange={onChange}
                    value={value}
                    isLoading={false}
                  />
                  <div className={styles.form_separatorLine} />
                </>
              </If>
            )}
          />

          <If condition={numberOfProductsWatch > productVariantsWatch.length}>
            <>
              <Button
                type="button"
                icon={<PlusInSquareIcon />}
                variant="default"
                onClick={onSelectProductsClick}
              >
                {t("Select Products")}
              </Button>
            </>
          </If>
        </>
      </If>
    </form>
  );
};

export default SharingScheduleForm;
